<template>
  <div class="relatorios-container">

    <!-- Navegação por Ícones -->
    <CardNavLocacao :pasta="pasta" class="mb-4" />

    <!-- Estado de carregamento -->
    <div v-if="carregando" class="text-center py-5 animar-entrada">
      <div class="spinner-border text-marrom" role="status"></div>
      <p class="text-muted mt-2">Carregando relatório...</p>
    </div>

    <!-- Aviso de erro ao carregar os dados -->
    <div v-if="erroCarregamento" class="alert alert-danger animar-entrada" role="alert">
      <strong>Falha ao carregar os dados do relatório.</strong><br />
      {{ erroCarregamento }}
    </div>

    <!-- Conteúdo do relatório -->
    <template v-else-if="!erroCarregamento">

      <!-- Cabeçalho da Pasta -->
      <div class="card relatorio-card mb-4 animar-entrada">
        <div class="relatorio-card-header d-flex justify-content-between align-items-center">
          <h3 class="mb-0">Relatório da Pasta {{ pasta }}</h3>
          <button class="btn-voltar" @click="$router.go(-1)">Voltar</button>
        </div>
        <div class="relatorio-card-body">
          <div class="row">
            <div class="col-md">
              <label>Referência do Imóvel</label>
              <div class="valor-info">{{ locacao.LOC_IMOVEL || '-' }}</div>
            </div>
            <div class="col-md">
              <label>Locador</label>
              <div class="valor-info">{{ locacao.LOC_LOCADOR || '-' }}</div>
            </div>
            <div class="col-md">
              <label>Locatário</label>
              <div class="valor-info">{{ locacao.LOC_LOCATARIO || '-' }}</div>
            </div>
            <div class="col-md">
              <label>Garantia</label>
              <div class="valor-info">{{ locacao.LOC_GARANTIA || '-' }}</div>
            </div>
            <div class="col-md">
              <label>Reajuste</label>
              <div class="valor-info">{{ locacao.LOC_REAJUSTE || '-' }}</div>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-md">
              <label>Aluguel Mensal</label>
              <div class="valor-info">{{ formatarMoeda(locacao.LOC_ALUGUEL) }}</div>
            </div>
            <div class="col-md">
              <label>Início do Contrato</label>
              <div class="valor-info">{{ formatarData(locacao.LOC_INICIO_CONTRATO) }}</div>
            </div>
            <div class="col-md">
              <label>Renovação do Contrato</label>
              <div class="valor-info">
                {{ formatarData(locacao.LOC_RENOVACAOCONTRATO) }}
                <span class="badge-status ml-1" :class="badgeRenovacaoContrato.classe">{{ badgeRenovacaoContrato.texto }}</span>
              </div>
            </div>
            <div class="col-md">
              <label>Renovação do Aluguel</label>
              <div class="valor-info">
                {{ formatarData(locacao.LOC_RENOVACAO_ALUGUEL) }}
                <span class="badge-status ml-1" :class="badgeRenovacaoAluguel.classe">{{ badgeRenovacaoAluguel.texto }}</span>
              </div>
            </div>
            <div class="col-md">
              <label>Vencimento</label>
              <div class="valor-info">Dia {{ locacao.LOC_VENCIMENTO || '-' }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Cards KPI com contagem animada -->
      <div class="row mb-4">
        <div class="col-md-3 mb-3" v-for="kpi in kpisVisiveis" :key="kpi.campo">
          <div class="card kpi-card animar-entrada" :style="{ animationDelay: kpi.delay + 'ms' }">
            <div class="kpi-bar" :style="{ background: kpi.cor }"></div>
            <div class="kpi-body">
              <span class="kpi-label">{{ kpi.label }}</span>
              <span class="kpi-valor" :style="{ color: kpi.cor }">{{ formatarKpi(kpi) }}</span>
              <span class="kpi-sub">{{ kpi.sub }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Gráficos: status (donut) + valores por mês (barras) -->
      <div class="row">
        <div class="col-md-4 mb-4">
          <div class="card relatorio-card h-100 animar-entrada">
            <div class="relatorio-card-header"><h5 class="mb-0">Status dos Boletos</h5></div>
            <div class="relatorio-card-body">
              <div v-if="boletos.total > 0" class="grafico-box"><canvas ref="chartStatus"></canvas></div>
              <div v-else class="sem-dados">Nenhum boleto emitido ainda.</div>
            </div>
          </div>
        </div>
        <div class="col-md-8 mb-4">
          <div class="card relatorio-card h-100 animar-entrada">
            <div class="relatorio-card-header"><h5 class="mb-0">Valores por Mês de Vencimento</h5></div>
            <div class="relatorio-card-body">
              <div v-if="boletos.total > 0" class="grafico-box"><canvas ref="chartMes"></canvas></div>
              <div v-else class="sem-dados">Nenhum boleto emitido ainda.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Gráfico de composição + resumo de repasses -->
      <div class="row">
        <div class="col-md-7 mb-4">
          <div class="card relatorio-card h-100 animar-entrada">
            <div class="relatorio-card-header"><h5 class="mb-0">Composição Média do Boleto</h5></div>
            <div class="relatorio-card-body">
              <div v-if="boletos.total > 0" class="grafico-box"><canvas ref="chartComposicao"></canvas></div>
              <div v-else class="sem-dados">Nenhum boleto emitido ainda.</div>
            </div>
          </div>
        </div>
        <div class="col-md-5 mb-4">
          <div class="card relatorio-card h-100 animar-entrada">
            <div class="relatorio-card-header"><h5 class="mb-0">Repasses ao Locador</h5></div>
            <div class="relatorio-card-body">
              <div class="repasse-resumo">
                <span class="kpi-label">Total repassado</span>
                <span class="repasses-total">{{ formatarMoeda(repasses.valor_total) }}</span>
                <span class="kpi-sub">{{ repasses.total }} repasse(s) realizado(s)</span>
              </div>
              <div v-if="repasses.historico.length" class="mt-3">
                <div class="repasse-item" v-for="r in repasses.historico" :key="r.id">
                  <span class="repasse-data">{{ formatarDataHora(r.data_repasse) }}</span>
                  <span class="valor-cell">{{ formatarMoeda(r.valor_repasse) }}</span>
                </div>
              </div>
              <div v-else class="sem-dados mt-3">Nenhum repasse registrado.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Projeção de boletos futuros -->
      <div class="row">
        <div class="col-md-12 mb-4">
          <div class="card relatorio-card animar-entrada">
            <div class="relatorio-card-header"><h5 class="mb-0">Projeção de Boletos Futuros</h5></div>
            <div class="relatorio-card-body">

              <!-- Estados de borda -->
              <div v-if="!projecao.pronto" class="sem-dados">{{ projecao.motivo }}</div>
              <div v-else-if="!projecao.meses.length" class="sem-dados">
                Sem boletos futuros estimados — todos os meses até o fim do contrato já possuem boleto gerado.
              </div>

              <!-- Conteúdo da projeção -->
              <div v-else>
                <!-- Resumo -->
                <div class="row mb-3">
                  <div class="col-md-4">
                    <div class="projecao-resumo">
                      <span class="kpi-label">Boletos estimados</span>
                      <span class="projecao-numero">{{ projecao.meses.length }}</span>
                      <span class="kpi-sub">até a renovação do contrato</span>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="projecao-resumo">
                      <span class="kpi-label">Total estimado</span>
                      <span class="projecao-numero">{{ formatarMoeda(projecao.total) }}</span>
                      <span class="kpi-sub">soma dos próximos boletos</span>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="projecao-resumo">
                      <span class="kpi-label">Média por boleto</span>
                      <span class="projecao-numero">{{ formatarMoeda(projecao.media) }}</span>
                      <span class="kpi-sub">incluindo parcelas abertas de IPTU e seguro</span>
                    </div>
                  </div>
                </div>

                <!-- Gráfico + lista mensal -->
                <div class="row">
                  <div class="col-md-8">
                    <div class="grafico-box"><canvas ref="chartProjecao"></canvas></div>
                  </div>
                  <div class="col-md-4">
                    <div class="projecao-lista">
                      <div class="projecao-item" v-for="m in projecao.meses" :key="m.chave">
                        <div class="projecao-item-info">
                          <span class="projecao-mes">{{ m.mes }}</span>
                          <span class="projecao-det" v-if="m.iptu || m.seguro">
                            <template v-if="m.iptu">+ IPTU {{ formatarMoeda(m.iptu) }}</template>
                            <template v-if="m.seguro"><template v-if="m.iptu"> · </template>+ Seguro {{ formatarMoeda(m.seguro) }}</template>
                          </span>
                          <span class="projecao-det" v-else>apenas aluguel + taxa</span>
                        </div>
                        <span class="valor-cell">{{ formatarMoeda(m.valor) }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="projecao-nota mt-3">
                  Estimativa baseada no fim do contrato ({{ projecao.fim }}), considerando
                  {{ locacao.LOC_IPTU_PARCELAS_ABERTAS || 0 }} parcela(s) de IPTU e
                  {{ locacao.LOC_SEGURO_PARCELAS_ABERTAS || 0 }} de seguro ainda abertas.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabelas informativas -->
      <div class="row">
        <div class="col-md-7 mb-4">
          <div class="card relatorio-card animar-entrada">
            <div class="relatorio-card-header"><h5 class="mb-0">Últimos Boletos</h5></div>
            <div class="relatorio-card-body">
              <div class="table-responsive">
                <table class="table table-docs text-center align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Vencimento</th>
                      <th>Valor</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!boletos.ultimos.length">
                      <td colspan="3" class="text-muted py-3">Nenhum boleto encontrado.</td>
                    </tr>
                    <tr
                      v-for="b in boletos.ultimos"
                      :key="b.id"
                      :class="{ 'linha-rascunho': b.enviado != 1 && b.pago != 1 }"
                    >
                      <td>{{ formatarData(b.vencimento) }}</td>
                      <td class="valor-cell">{{ formatarMoeda(b.valor_total) }}</td>
                      <td>
                        <span v-if="b.pago == 1" class="badge-status pago-sim">Pago</span>
                        <span v-else-if="b.enviado == 1" class="badge-status enviado-badge">Enviado</span>
                        <span v-else class="badge-status rascunho-badge">Rascunho</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-5 mb-4">
          <div class="card relatorio-card animar-entrada">
            <div class="relatorio-card-header"><h5 class="mb-0">Situação Contratual</h5></div>
            <div class="relatorio-card-body">
              <div class="situacao-item">
                <div class="situacao-info">
                  <span class="kpi-label">Meses de Contrato</span>
                  <span class="situacao-valor">{{ locacao.LOC_TEMPO_CONTRATO || 0 }} meses</span>
                </div>
              </div>
              <div class="situacao-item">
                <div class="situacao-info">
                  <span class="kpi-label">Parcelas IPTU abertas</span>
                  <span class="situacao-valor">{{ locacao.LOC_IPTU_PARCELAS_ABERTAS || 0 }} / {{ locacao.LOC_IPTU_TOTAL_PARCELAS || 0 }}</span>
                </div>
                <div class="progresso" :style="{ width: pctIptu + '%', background: corIptu }"></div>
              </div>
              <div class="situacao-item">
                <div class="situacao-info">
                  <span class="kpi-label">Parcelas Seguro abertas</span>
                  <span class="situacao-valor">{{ locacao.LOC_SEGURO_PARCELAS_ABERTAS || 0 }} / {{ locacao.LOC_SEGURO_TOTAL_PARCELAS || 0 }}</span>
                </div>
                <div class="progresso" :style="{ width: pctSeguro + '%', background: corSeguro }"></div>
              </div>
              <div class="situacao-item">
                <div class="situacao-info">
                  <span class="kpi-label">Administração da pasta</span>
                  <span class="situacao-valor">{{ formatarMoeda(locacao.LOC_VALOR_ADM) }}</span>
                </div>
              </div>
              <div class="situacao-item">
                <div class="situacao-info">
                  <span class="kpi-label">Taxa do boleto</span>
                  <span class="situacao-valor">{{ formatarMoeda(locacao.LOC_BOLETO) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </template>

    <!-- Navegação por Ícones (rodapé) -->
    <CardNavLocacao :pasta="pasta" class="mt-4" />
  </div>
</template>

<script>
import axios from "axios";
import Chart from "chart.js/auto";
import CardNavLocacao from "@/components/CardNavLocacao";

export default {
  name: "Relatorios",
  components: { CardNavLocacao },

  data() {
    return {
      baseUrl: "https://www2.royaljardim.com.br/sistema/",
      pasta: "",
      carregando: true,
      erroCarregamento: "",
      // Dados brutos retornados pelo endpoint agregado
      locacao: {},
      boletos: {
        total: 0,
        pagos: 0,
        em_aberto: 0,
        rascunhos: 0,
        valor_recebido: 0,
        valor_em_aberto: 0,
        por_mes: [],
        composicao: {},
        ultimos: [],
      },
      repasses: { total: 0, valor_total: 0, historico: [] },
      // Valores exibidos nos KPIs (animados) — cada chave referencia um campo abaixo
      kpis: {
        boletos: { exibir: 0, alvo: 0 },
        recebido: { exibir: 0, alvo: 0 },
        emAberto: { exibir: 0, alvo: 0 },
        inadimplencia: { exibir: 0, alvo: 0 },
        aluguel: { exibir: 0, alvo: 0 },
        repasses: { exibir: 0, alvo: 0 },
        iptuAbertas: { exibir: 0, alvo: 0 },
        seguroAbertas: { exibir: 0, alvo: 0 },
      },
      // Referências dos gráficos criados (para destruir ao sair da tela)
      chartStatus: null,
      chartMes: null,
      chartComposicao: null,
      chartProjecao: null,
    };
  },

  computed: {
    // Configuração visual de cada card de KPI
    kpisVisiveis() {
      return [
        { campo: "boletos", label: "Boletos Emitidos", cor: "#4C1C17", delay: 0, sub: "no total" },
        { campo: "recebido", label: "Total Recebido", cor: "#28a745", delay: 70, sub: "boletos pagos" },
        { campo: "emAberto", label: "Em Aberto", cor: "#dc3545", delay: 140, sub: "a receber" },
        { campo: "inadimplencia", label: "Inadimplência", cor: "#f59e0b", delay: 210, sub: "do total cobrado" },
        { campo: "aluguel", label: "Aluguel Mensal", cor: "#0d6efd", delay: 280, sub: "valor do contrato" },
        { campo: "repasses", label: "Total Repassado", cor: "#198754", delay: 350, sub: "ao locador" },
        { campo: "iptuAbertas", label: "Parcelas IPTU Abertas", cor: "#0dcaf0", delay: 420, sub: this.locacao.LOC_IPTU_TOTAL_PARCELAS ? "de " + this.locacao.LOC_IPTU_TOTAL_PARCELAS + " total" : "sem registro" },
        { campo: "seguroAbertas", label: "Parcelas Seguro Abertas", cor: "#6f42c1", delay: 490, sub: this.locacao.LOC_SEGURO_TOTAL_PARCELAS ? "de " + this.locacao.LOC_SEGURO_TOTAL_PARCELAS + " total" : "sem registro" },
      ];
    },
    // Badges de renovação do contrato
    badgeRenovacaoContrato() {
      return this.badgeDias(this.locacao.LOC_RENOVACAOCONTRATO);
    },
    // Badges de renovação do aluguel
    badgeRenovacaoAluguel() {
      return this.badgeDias(this.locacao.LOC_RENOVACAO_ALUGUEL);
    },
    // Percentual de parcelas de IPTU já pagas
    pctIptu() {
      const total = parseFloat(this.locacao.LOC_IPTU_TOTAL_PARCELAS);
      const abertas = parseFloat(this.locacao.LOC_IPTU_PARCELAS_ABERTAS);
      if (!total) return 0;
      // Quanto menor o número de abertas, melhor a situação
      return Math.round(((total - abertas) / total) * 100);
    },
    corIptu() {
      return this.pctIptu >= 70 ? "#28a745" : this.pctIptu >= 40 ? "#f59e0b" : "#dc3545";
    },
    // Percentual de parcelas de seguro já pagas
    pctSeguro() {
      const total = parseFloat(this.locacao.LOC_SEGURO_TOTAL_PARCELAS);
      const abertas = parseFloat(this.locacao.LOC_SEGURO_PARCELAS_ABERTAS);
      if (!total) return 0;
      return Math.round(((total - abertas) / total) * 100);
    },
    corSeguro() {
      return this.pctSeguro >= 70 ? "#28a745" : this.pctSeguro >= 40 ? "#f59e0b" : "#dc3545";
    },
    // Projeção de boletos futuros ainda não gerados, estimados a partir do fim
    // do contrato e das parcelas abertas de IPTU e seguro.
    projecao() {
      const l = this.locacao;
      const aluguel = parseFloat(l.LOC_ALUGUEL) || 0;
      const taxa = parseFloat(l.LOC_BOLETO) || 0;
      const iptuValor = parseFloat(l.LOC_IPTU_VALOR) || 0;
      const seguroValor = parseFloat(l.LOC_SEGURO_VALOR) || 0;
      const tempo = parseInt(l.LOC_TEMPO_CONTRATO, 10);

      // Data base do contrato: renovação quando existir, senão o início
      const base = l.LOC_RENOVACAOCONTRATO || l.LOC_INICIO_CONTRATO;
      const baseDt = base ? new Date(base) : null;

      // Sem dados suficientes para projetar
      if (!baseDt || isNaN(baseDt) || !tempo) {
        return { pronto: false, motivo: "Sem dados suficientes para projetar os boletos futuros." };
      }

      // Fim do contrato = base + tempo em meses (mesmo cálculo da ficha de locação)
      const fim = new Date(baseDt);
      fim.setMonth(fim.getMonth() + tempo);

      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      // Contrato já encerrado
      if (fim <= hoje) {
        return {
          pronto: false,
          motivo: "Contrato encerrado em " + fim.toLocaleDateString("pt-BR") + "; sem boletos futuros estimados.",
        };
      }

      // Chaves dos meses que já possuem boleto gerado (YYYY-MM)
      const mesesComBoleto = (this.boletos.por_mes || []).map(m => String(m.mes));

      // Percorre cada mês (do próximo mês até o fim do contrato). O número de
      // iterações é limitado pela diferença em meses entre hoje e o fim do contrato.
      let ano = hoje.getFullYear();
      let mes = hoje.getMonth();
      let iptuRestante = parseInt(l.LOC_IPTU_PARCELAS_ABERTAS, 10) || 0;
      let seguroRestante = parseInt(l.LOC_SEGURO_PARCELAS_ABERTAS, 10) || 0;
      const meses = [];
      const totalMeses = (fim.getFullYear() - hoje.getFullYear()) * 12 + (fim.getMonth() - hoje.getMonth());

      for (let n = 0; n < totalMeses; n++) {
        // Avança para o mês seguinte (parte do mês corrente)
        ano = mes === 11 ? ano + 1 : ano;
        mes = (mes + 1) % 12;

        const chave = ano + "-" + String(mes + 1).padStart(2, "0");

        // Pula meses que já possuem boleto gerado
        if (mesesComBoleto.indexOf(chave) !== -1) continue;

        // Compõe o valor estimado do boleto do mês
        const item = { chave, mes: String(mes + 1).padStart(2, "0") + "/" + ano, valor: aluguel + taxa, iptu: 0, seguro: 0 };
        if (iptuRestante > 0) { item.valor += iptuValor; item.iptu = iptuValor; iptuRestante--; }
        if (seguroRestante > 0) { item.valor += seguroValor; item.seguro = seguroValor; seguroRestante--; }

        meses.push(item);
      }

      const total = meses.reduce((s, m) => s + m.valor, 0);

      return {
        pronto: true,
        fim: fim.toLocaleDateString("pt-BR"),
        meses,
        qtd: meses.length,
        total,
        media: meses.length ? total / meses.length : 0,
      };
    },
  },

  mounted() {
    this.pasta = this.$route.params.id;
    this.carregarDados();
  },

  beforeDestroy() {
    // Destroi os gráficos para evitar vazamento de memória ao trocar de rota
    if (this.chartStatus) { this.chartStatus.destroy(); }
    if (this.chartMes) { this.chartMes.destroy(); }
    if (this.chartComposicao) { this.chartComposicao.destroy(); }
    if (this.chartProjecao) { this.chartProjecao.destroy(); }
  },

  methods: {
    // Busca os dados agregados do relatório
    async carregarDados() {
      try {
        const res = await axios.post(this.baseUrl + "relatorios/estatisticas", new URLSearchParams({
          pasta: this.pasta,
        }));
        const dados = res.data;
        if (dados && dados.erro) {
          this.erroCarregamento = dados.erro;
          return;
        }
        this.locacao = dados.locacao || {};
        this.boletos = Object.assign(this.boletos, dados.boletos || {});
        this.repasses = Object.assign(this.repasses, dados.repasses || {});
        this.animarKPIs();
      } catch (e) {
        console.error("Erro ao carregar relatório:", e);
        this.erroCarregamento = "Não foi possível consultar o servidor. Confirme se o controller " +
          "'Relatorios.php' está publicado em " + this.baseUrl + " (HTTP " +
          (e.response ? e.response.status : "sem resposta") + ").";
      } finally {
        this.carregando = false;
        // Aguarda dois ciclos do Vue: garante que o conteúdo v-else já está no DOM
        // antes de criar os gráficos (evita canvas com tamanho 0).
        this.$nextTick(() => {
          this.$nextTick(() => this.criarGraficos());
        });
      }
    },

    // Dispara a contagem animada dos valores dos KPIs
    animarKPIs() {
      this.animarKpi("boletos", this.boletos.total);
      this.animarKpi("recebido", this.boletos.valor_recebido);
      this.animarKpi("emAberto", this.boletos.valor_em_aberto);
      const inad = this.boletos.total ? (this.boletos.em_aberto / this.boletos.total) * 100 : 0;
      this.animarKpi("inadimplencia", inad);
      this.animarKpi("aluguel", this.locacao.LOC_ALUGUEL);
      this.animarKpi("repasses", this.repasses.valor_total);
      this.animarKpi("iptuAbertas", this.locacao.LOC_IPTU_PARCELAS_ABERTAS);
      this.animarKpi("seguroAbertas", this.locacao.LOC_SEGURO_PARCELAS_ABERTAS);
    },

    // Contagem progressiva usando requestAnimationFrame com easing
    animarKpi(campo, alvo) {
      const item = this.kpis[campo];
      item.alvo = parseFloat(alvo) || 0;
      const inicio = performance.now();
      const duracao = 900;
      const passo = (agora) => {
        let progresso = Math.min((agora - inicio) / duracao, 1);
        // easing de saída suave (cubic ease-out)
        progresso = 1 - Math.pow(1 - progresso, 3);
        item.exibir = item.alvo * progresso;
        if (progresso < 1) {
          requestAnimationFrame(passo);
        } else {
          item.exibir = item.alvo;
        }
      };
      requestAnimationFrame(passo);
    },

    // Formata o valor exibido de cada KPI de acordo com o tipo (moeda/percentual/contagem)
    formatarKpi(kpi) {
      const valor = this.kpis[kpi.campo].exibir;
      switch (kpi.campo) {
        case "recebido":
        case "emAberto":
        case "aluguel":
        case "repasses":
          return this.formatarMoeda(valor);
        case "inadimplencia":
          return valor.toFixed(1) + "%";
        default:
          return Math.round(valor).toString();
      }
    },

    // Cria os três gráficos após os dados carregarem. Cada criação é protegida
    // por try/catch para que uma falha isolada não derrube os demais gráficos.
    criarGraficos() {
      if (this.boletos.total > 0) {
        this.criarGraficoStatus();
        this.criarGraficoMes();
        this.criarGraficoComposicao();
      }
      this.criarGraficoProjecao();
    },

    // Donut de status: pagos x em aberto x rascunhos
    criarGraficoStatus() {
      const ctx = this.$refs.chartStatus;
      if (!ctx) return;
      try {
        if (this.chartStatus) this.chartStatus.destroy();
        this.chartStatus = new Chart(ctx, {
          type: "doughnut",
          data: {
            labels: ["Pagos", "Em aberto", "Rascunho"],
            datasets: [{
              data: [this.boletos.pagos, this.boletos.em_aberto, this.boletos.rascunhos],
              backgroundColor: ["#28a745", "#dc3545", "#f59e0b"],
              borderWidth: 0,
              hoverOffset: 8,
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { animateRotate: true, animateScale: true },
            plugins: {
              legend: {
                position: "bottom",
                labels: { color: "#333", font: { size: 12 }, padding: 14, usePointStyle: true },
              },
              tooltip: {
                callbacks: {
                  label: (item) => {
                    const valor = this.boletos[item.dataIndex === 0 ? "pagos" : item.dataIndex === 1 ? "em_aberto" : "rascunhos"];
                    return " " + item.label + ": " + valor + " boleto(s)";
                  },
                },
              },
            },
          },
        });
      } catch (e) {
        console.error("Erro ao criar gráfico de status:", e);
      }
    },

    // Barras: valores cobrados x recebidos por mês de vencimento
    criarGraficoMes() {
      const ctx = this.$refs.chartMes;
      if (!ctx) return;
      try {
        if (this.chartMes) this.chartMes.destroy();
        const rotulos = this.boletos.por_mes.map(m => {
          const partes = String(m.mes).split("-");
          return partes[1] + "/" + partes[0];
        });
        const cobrado = this.boletos.por_mes.map(m => parseFloat(m.valor) || 0);
        const recebido = this.boletos.por_mes.map(m => parseFloat(m.recebido) || 0);

        this.chartMes = new Chart(ctx, {
          type: "bar",
          data: {
            labels: rotulos,
            datasets: [
              { label: "Cobrado", data: cobrado, backgroundColor: "#4C1C17", borderRadius: 4 },
              { label: "Recebido", data: recebido, backgroundColor: "#28a745", borderRadius: 4 },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { intersect: false, mode: "index" },
            plugins: { legend: { position: "top" } },
            scales: {
              x: { grid: { display: false } },
              y: {
                beginAtZero: true,
                grid: { color: "rgba(0,0,0,0.05)" },
                ticks: { callback: (v) => this.moedaCurta(v) },
              },
            },
          },
        });
      } catch (e) {
        console.error("Erro ao criar gráfico por mês:", e);
      }
    },

    // Barra empilhada: composição média de cada componente do boleto
    criarGraficoComposicao() {
      const ctx = this.$refs.chartComposicao;
      if (!ctx) return;
      try {
        if (this.chartComposicao) this.chartComposicao.destroy();
        const comp = this.boletos.composicao || {};
        const itens = [
          { label: "Aluguel", campo: "aluguel", cor: "#4C1C17" },
          { label: "IPTU", campo: "iptu", cor: "#0d6efd" },
          { label: "Condomínio", campo: "condominio", cor: "#0dcaf0" },
          { label: "Seguro", campo: "seguro", cor: "#f59e0b" },
          { label: "Fiança", campo: "fianca", cor: "#dc3545" },
          { label: "Taxa Boleto", campo: "taxa", cor: "#6f42c1" },
        ];

        this.chartComposicao = new Chart(ctx, {
          type: "bar",
          data: {
            labels: ["Média por boleto"],
            datasets: itens.map(it => ({
              label: it.label,
              data: [parseFloat(comp[it.campo]) || 0],
              backgroundColor: it.cor,
              borderRadius: 3,
            })),
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { position: "bottom" } },
            scales: {
              x: { stacked: true, grid: { display: false } },
              y: {
                stacked: true,
                beginAtZero: true,
                grid: { color: "rgba(0,0,0,0.05)" },
                ticks: { callback: (v) => this.moedaCurta(v) },
              },
            },
          },
        });
      } catch (e) {
        console.error("Erro ao criar gráfico de composição:", e);
      }
    },

    // Barras: valores estimados dos boletos futuros por mês
    criarGraficoProjecao() {
      const ctx = this.$refs.chartProjecao;
      if (!ctx) return;
      if (!this.projecao.pronto || !this.projecao.meses.length) return;
      try {
        if (this.chartProjecao) this.chartProjecao.destroy();

        this.chartProjecao = new Chart(ctx, {
          type: "bar",
          data: {
            labels: this.projecao.meses.map(m => m.mes),
            datasets: [{
              label: "Boleto estimado",
              data: this.projecao.meses.map(m => parseFloat(m.valor) || 0),
              backgroundColor: "#4C1C17",
              borderRadius: 4,
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  // Detalha a composição do boleto estimado no tooltip
                  label: (item) => {
                    const m = this.projecao.meses[item.dataIndex];
                    if (!m) return "";
                    let texto = " Total: " + this.formatarMoeda(m.valor);
                    if (m.iptu) texto = " IPTU: " + this.formatarMoeda(m.iptu) + texto;
                    if (m.seguro) texto = " Seguro: " + this.formatarMoeda(m.seguro) + " ·" + texto;
                    return texto;
                  },
                },
              },
            },
            scales: {
              x: { grid: { display: false } },
              y: {
                beginAtZero: true,
                grid: { color: "rgba(0,0,0,0.05)" },
                ticks: { callback: (v) => this.moedaCurta(v) },
              },
            },
          },
        });
      } catch (e) {
        console.error("Erro ao criar gráfico de projeção:", e);
      }
    },

    // Retorna badge (classe + texto) com base em quantos dias faltam para a data
    badgeDias(dataStr) {
      if (!dataStr) return { classe: "pago-nao", texto: "Sem data" };
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const alvo = new Date(dataStr);
      alvo.setHours(0, 0, 0, 0);
      const diff = alvo - hoje;
      const dias = Math.ceil(diff / (1000 * 60 * 60 * 24));
      if (dias < 0) return { classe: "pago-nao", texto: "Vencido" };
      if (dias === 0) return { classe: "rascunho-badge", texto: "Hoje" };
      if (dias <= 30) return { classe: "rascunho-badge", texto: "+" + dias + " dias" };
      return { classe: "pago-sim", texto: "+" + dias + " dias" };
    },

    // Formata número/string para moeda brasileira
    formatarMoeda(v) {
      const numero = parseFloat(v) || 0;
      return numero.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },

    // Formata valor com no máximo 2 casas, sem centavos desnecessários (para eixos)
    moedaCurta(v) {
      return "R$ " + (parseFloat(v) || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 });
    },

    // Formata data ISO (YYYY-MM-DD ou datetime) para dd/mm/yyyy
    formatarData(d) {
      if (!d) return "-";
      const dt = new Date(d);
      if (isNaN(dt)) return d;
      return dt.toLocaleDateString("pt-BR");
    },

    // Formata data/hora para dd/mm/yyyy hh:mm
    formatarDataHora(d) {
      if (!d) return "-";
      const dt = new Date(d);
      if (isNaN(dt)) return d;
      return dt.toLocaleString("pt-BR");
    },
  },
};
</script>

<style scoped>
/* ===== Cards base ===== */
.relatorio-card {
  border: 1px solid #e8e4e3;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.relatorio-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.09);
}
.relatorio-card-header {
  background: #4C1C17;
  color: #fff;
  padding: 12px 20px;
}
.relatorio-card-header h3,
.relatorio-card-header h5 {
  color: #fff;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.relatorio-card-header h3 { font-size: 17px; }
.relatorio-card-header h5 { font-size: 14px; }
.relatorio-card-body {
  padding: 20px;
  background: #fff;
}
.relatorio-card label {
  color: #4C1C17;
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.valor-info {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

/* ===== Botão voltar ===== */
.btn-voltar {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.4);
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  transition: all 0.2s;
}
.btn-voltar:hover {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.6);
}

/* ===== Cards KPI ===== */
.kpi-card {
  border: 1px solid #e8e4e3;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  height: 100%;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.1);
}
.kpi-bar {
  height: 5px;
  width: 100%;
}
.kpi-body {
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
}
.kpi-label {
  color: #666;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.kpi-valor {
  font-size: 26px;
  font-weight: 800;
  line-height: 1.2;
  margin: 4px 0 2px;
}
.kpi-sub {
  color: #999;
  font-size: 12px;
}

/* ===== Animações de entrada ===== */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.animar-entrada {
  animation: fadeInUp 0.6s ease both;
}

/* ===== Badge Status (reaproveitado do projeto) ===== */
.badge-status {
  display: inline-block;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.pago-sim {
  background: #e8f5e9;
  color: #28a745;
}
.enviado-badge {
  background: #e8f5e9;
  color: #28a745;
}
.rascunho-badge {
  background: #fff8e1;
  color: #92400e;
}
.pago-nao {
  background: #ffe5e5;
  color: #dc3545;
}
.linha-rascunho {
  border-left: 3px solid #f59e0b;
  background-color: #fffdf5;
}

/* ===== Tabela ===== */
.table-docs thead {
  background: #4C1C17;
  color: #fff;
}
.table-docs th {
  padding: 12px 16px;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
}
.table-docs td {
  padding: 12px 16px;
  vertical-align: middle;
  font-size: 14px;
}
.table-docs tbody tr:hover {
  background: #f8f4f3;
}
.valor-cell {
  font-weight: 700;
  color: #4C1C17;
}

/* ===== Seções auxiliares ===== */
/* Recipiente do canvas: altura fixa garante que o gráfico nunca renderize com 0px */
.grafico-box {
  position: relative;
  height: 300px;
}
.grafico-box:first-child {
  height: 240px;
}
.sem-dados {
  color: #999;
  font-size: 14px;
  padding: 24px 0;
  text-align: center;
}
.text-marrom {
  color: #4C1C17;
}

/* Resumo de repasses */
.repasse-resumo {
  display: flex;
  flex-direction: column;
  padding-bottom: 14px;
  border-bottom: 1px dashed #e8e4e3;
}
.repasses-total {
  font-size: 26px;
  font-weight: 800;
  color: #4C1C17;
  line-height: 1.2;
  margin: 4px 0 2px;
}
.repasse-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 2px;
  border-bottom: 1px solid #f5f2f1;
  font-size: 14px;
}
.repasse-item:last-child {
  border-bottom: none;
}
.repasse-data {
  color: #666;
}

/* Situação contratual */
.situacao-item {
  margin-bottom: 18px;
}
.situacao-item:last-child {
  margin-bottom: 0;
}
.situacao-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.situacao-valor {
  font-size: 14px;
  font-weight: 700;
  color: #333;
}
.progresso {
  height: 8px;
  border-radius: 8px;
  transition: width 1s ease;
}

/* ===== Projeção de Boletos Futuros ===== */
.projecao-resumo {
  display: flex;
  flex-direction: column;
  background: #faf7f6;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 14px 18px;
}
.projecao-numero {
  font-size: 24px;
  font-weight: 800;
  color: #4C1C17;
  line-height: 1.2;
  margin: 4px 0 2px;
}
.projecao-lista {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 6px 14px;
}
.projecao-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 2px;
  border-bottom: 1px solid #f5f2f1;
  font-size: 14px;
}
.projecao-item:last-child {
  border-bottom: none;
}
.projecao-item-info {
  display: flex;
  flex-direction: column;
  line-height: 1.35;
}
.projecao-mes {
  font-weight: 700;
  color: #333;
}
.projecao-det {
  color: #999;
  font-size: 12px;
}
.projecao-nota {
  color: #888;
  font-size: 12px;
  background: #faf7f6;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 10px 14px;
}
</style>